import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export const CloudMigrationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-950 dark:bg-gray-900">
      <Helmet>
        <title>Cloud Migration Success Story | KOBRALABS</title>
        <meta name="description" content="Discover how KOBRALABS helped Global Retail Corp achieve 40% cost reduction and 99.9% uptime through comprehensive cloud migration." />
        <meta name="keywords" content="cloud migration, AWS, cost reduction, scalability, retail technology" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20"></div>
        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Cloud Migration Success Story
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Global Retail Corp achieved 40% cost reduction and 99.9% uptime
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">Cloud Computing</span>
                <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full">AWS</span>
                <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">Migration</span>
                <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full">Retail</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-8">Executive Summary</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Challenge</h3>
                <p className="text-gray-300">
                  Global Retail Corp, a multinational retail chain with 500+ stores across 15 countries, 
                  was struggling with their aging on-premises infrastructure. Their legacy systems were 
                  causing frequent downtime, high operational costs, and limited scalability during peak 
                  shopping seasons.
                </p>
              </div>
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">The Solution</h3>
                <p className="text-gray-300">
                  KOBRALABS designed and executed a comprehensive cloud migration strategy using AWS, 
                  implementing modern cloud-native architectures, automated scaling, and robust disaster 
                  recovery solutions.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Analysis */}
      <section className="py-16 px-4 bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Detailed Analysis</h2>
            
            {/* Technical Challenges */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Technical Challenges</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3">Infrastructure Limitations</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Aging hardware with 5+ year lifecycle</li>
                    <li>• Limited storage capacity (2TB total)</li>
                    <li>• Single points of failure in critical systems</li>
                    <li>• Manual backup processes</li>
                    <li>• No disaster recovery plan</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-red-400 mb-3">Performance Issues</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 15-20 second page load times</li>
                    <li>• 95% CPU utilization during peak hours</li>
                    <li>• Database connection timeouts</li>
                    <li>• Inconsistent user experience across regions</li>
                    <li>• Limited concurrent user capacity</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Solution Architecture */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Solution Architecture</h3>
              <div className="bg-gray-800 p-8 rounded-xl">
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-green-400 mb-4">AWS Infrastructure</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• EC2 Auto Scaling Groups</li>
                      <li>• RDS Multi-AZ deployment</li>
                      <li>• S3 for static content</li>
                      <li>• CloudFront CDN</li>
                      <li>• Route 53 for DNS</li>
                      <li>• VPC with private subnets</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-purple-400 mb-4">Security & Compliance</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• AWS WAF protection</li>
                      <li>• SSL/TLS encryption</li>
                      <li>• IAM role-based access</li>
                      <li>• CloudTrail logging</li>
                      <li>• PCI DSS compliance</li>
                      <li>• Regular security audits</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-orange-400 mb-4">Monitoring & Operations</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• CloudWatch monitoring</li>
                      <li>• Automated alerting</li>
                      <li>• Centralized logging</li>
                      <li>• Performance dashboards</li>
                      <li>• 24/7 support coverage</li>
                      <li>• Incident response procedures</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Migration Process */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Migration Process</h3>
              <div className="space-y-6">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-3">Phase 1: Assessment & Planning (4 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Comprehensive analysis of existing infrastructure, application dependencies, and business requirements.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Infrastructure discovery and mapping</li>
                    <li>• Application dependency analysis</li>
                    <li>• Performance baseline establishment</li>
                    <li>• Risk assessment and mitigation planning</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-3">Phase 2: Infrastructure Setup (6 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Building the new AWS infrastructure with security, monitoring, and automation in place.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• VPC and networking setup</li>
                    <li>• Security groups and IAM configuration</li>
                    <li>• Database migration preparation</li>
                    <li>• Monitoring and alerting setup</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-purple-400 mb-3">Phase 3: Application Migration (8 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Gradual migration of applications with minimal downtime using blue-green deployment strategy.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Database migration with zero downtime</li>
                    <li>• Application deployment using CI/CD</li>
                    <li>• Load balancer configuration</li>
                    <li>• Performance optimization</li>
                  </ul>
                </div>
                
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-orange-400 mb-3">Phase 4: Testing & Optimization (4 weeks)</h4>
                  <p className="text-gray-300 mb-3">
                    Comprehensive testing, performance tuning, and optimization of the new cloud environment.
                  </p>
                  <ul className="text-gray-300 space-y-1">
                    <li>• Load testing and performance validation</li>
                    <li>• Security penetration testing</li>
                    <li>• Disaster recovery testing</li>
                    <li>• Cost optimization analysis</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Results & Metrics */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Results & Metrics</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-6 rounded-xl border border-green-500/30">
                  <div className="text-3xl font-bold text-green-400 mb-2">40%</div>
                  <div className="text-gray-300">Cost Reduction</div>
                  <div className="text-sm text-gray-400 mt-2">Annual operational costs</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 p-6 rounded-xl border border-blue-500/30">
                  <div className="text-3xl font-bold text-blue-400 mb-2">99.9%</div>
                  <div className="text-gray-300">System Uptime</div>
                  <div className="text-sm text-gray-400 mt-2">Improved from 95%</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 p-6 rounded-xl border border-purple-500/30">
                  <div className="text-3xl font-bold text-purple-400 mb-2">50%</div>
                  <div className="text-gray-300">Faster Deployment</div>
                  <div className="text-sm text-gray-400 mt-2">Reduced from 4 hours to 2 hours</div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 p-6 rounded-xl border border-orange-500/30">
                  <div className="text-3xl font-bold text-orange-400 mb-2">3x</div>
                  <div className="text-gray-300">Scalability</div>
                  <div className="text-sm text-gray-400 mt-2">Peak traffic handling capacity</div>
                </div>
              </div>
            </div>

            {/* Business Impact */}
            <div className="mb-16">
              <h3 className="text-2xl font-semibold text-white mb-6">Business Impact</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-400 mb-4">Operational Benefits</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• Reduced IT staff workload by 30%</li>
                    <li>• Eliminated manual backup processes</li>
                    <li>• Improved incident response time by 60%</li>
                    <li>• Enhanced data security and compliance</li>
                    <li>• Streamlined disaster recovery procedures</li>
                  </ul>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-400 mb-4">Customer Experience</h4>
                  <ul className="text-gray-300 space-y-2">
                    <li>• 70% improvement in page load times</li>
                    <li>• 25% increase in online sales</li>
                    <li>• 40% reduction in customer complaints</li>
                    <li>• Improved mobile app performance</li>
                    <li>• Better availability during peak seasons</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Lessons Learned */}
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Lessons Learned</h3>
              <div className="bg-gray-800 p-6 rounded-xl">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-yellow-400 mb-3">Key Success Factors</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Comprehensive planning and assessment</li>
                      <li>• Phased migration approach</li>
                      <li>• Strong stakeholder communication</li>
                      <li>• Robust testing and validation</li>
                      <li>• Continuous monitoring and optimization</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-red-400 mb-3">Challenges Overcome</h4>
                    <ul className="text-gray-300 space-y-2">
                      <li>• Legacy application compatibility</li>
                      <li>• Data migration complexity</li>
                      <li>• Team skill development</li>
                      <li>• Change management resistance</li>
                      <li>• Performance optimization requirements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Transform Your Infrastructure?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Let KOBRALABS help you achieve similar results with a comprehensive cloud migration strategy 
              tailored to your business needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Get Started Today
              </Link>
              <Link
                to="/case-studies"
                className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                View All Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}; 